import { Injectable, InjectionToken, computed, inject, signal } from '@angular/core';
import { BRAND_REGISTRY, Brand, BrandConfig, VALID_BRANDS } from './branding.model';

export const ALLOW_BRANDING_QUERY_PARAM = new InjectionToken<boolean>('allowBrandingQueryParam', {
  factory: () => false,
});

export interface HostInfo {
  sparte: string | null;
  brand: string | null;
}

// Schema: <sparte>[-<brand>].vergleich.<domain>, e.g. kfz.vergleich.cpit.app, kfz-mlp.vergleich.cpit.app
const VERGLEICH_HOST = /^(\w+)(?:-(\w+))?\.vergleich\./;
// Legacy, still served: [<brand>-]<sparte>.endkundenrechner.<domain>, e.g. mlp-kfz.endkundenrechner.cpit.dev
const LEGACY_HOST = /^(?:(\w+)-)?(\w+)\.endkundenrechner\./;

/**
 * Reads Sparte and brand from the hostname. The two schemas have the parts in opposite order,
 * so each pattern is anchored to its own base domain.
 */
export function parseHost(hostname: string): HostInfo {
  const host = hostname.toLowerCase();

  const current = host.match(VERGLEICH_HOST);
  if (current) return { sparte: current[1], brand: current[2] ?? null };

  const legacy = host.match(LEGACY_HOST);
  if (legacy) return { sparte: legacy[2], brand: legacy[1] ?? null };

  return { sparte: null, brand: null };
}

@Injectable({ providedIn: 'root' })
export class BrandingService {
  private allowQueryParam = inject(ALLOW_BRANDING_QUERY_PARAM);
  private registry = inject(BRAND_REGISTRY);

  private readonly _brand = signal<Brand | null>(null);
  readonly brand = this._brand.asReadonly();

  private readonly _sparte = signal<string | null>(null);
  /** Sparte from the subdomain; null on localhost and on the bare legacy root domain. */
  readonly sparte = this._sparte.asReadonly();

  readonly config = computed<BrandConfig | null>(() => {
    const b = this._brand();
    return b ? (this.registry[b] ?? null) : null;
  });

  bootstrap(): void {
    const { sparte, brand } = parseHost(window.location.hostname);
    this._sparte.set(sparte);

    if (brand && this.isValidBrand(brand)) {
      this.setBrand(brand);
      return;
    }

    if (this.allowQueryParam) {
      const url = new URL(window.location.href);
      const param = url.searchParams.get('branding');

      if (param && this.isValidBrand(param)) {
        this.setBrand(param);
        url.searchParams.delete('branding');
        window.history.replaceState({}, '', url.toString());
      }
    }
  }

  getBrand(): Brand | null {
    return this._brand();
  }

  private setBrand(b: Brand): void {
    this._brand.set(b);
    this.applyTheme(b);
  }

  private applyTheme(b: Brand): void {
    document.documentElement.setAttribute('data-branding', b);
  }

  private isValidBrand(value: string): value is Brand {
    return (VALID_BRANDS as readonly string[]).includes(value);
  }
}

export function initBranding(): () => void {
  return () => {
    const brandingService = inject(BrandingService);
    brandingService.bootstrap();
  };
}
